/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { IconProps, Theme } from '@coursera/cds-core';
import { Typography2, useTheme } from '@coursera/cds-core';

import type { GoalNames } from 'bundles/enterprise-learner-onboarding/components/LearnerOnboardingContainer';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

const tile = ({ isSelected, theme }: { isSelected?: boolean; theme: Theme }) =>
  css({
    width: '164px',
    height: '140px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(12),
    flexDirection: 'column',
    border: isSelected ? `1px solid ${theme.palette.blue[600]}` : `1px solid ${theme.palette.gray[500]}`,
    background: isSelected ? '#EBF3FF' : 'var(--cds-color-white-0)',
    borderRadius: theme.spacing(16),
    padding: theme.spacing(24),
    '&:hover': {
      border: `1px solid ${theme.palette.blue[600]}`,
      boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.16)',
      transform: 'translateY(-4px)',
      transition: 'all 0.1s',
      cursor: 'pointer',
    },
  });

const disabledTile = ({ theme }: { theme: Theme }) =>
  css({
    width: '164px',
    height: '140px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(12),
    flexDirection: 'column',
    border: `1px solid ${theme.palette.gray[300]}`,
    background: 'var(--cds-color-white-0)',
    borderRadius: theme.spacing(16),
    padding: theme.spacing(24),
  });

export type GoalTileProps = {
  title: string;
  name: GoalNames;
  Icon?: React.ComponentType<IconProps>;
  isSelected?: boolean;
  onClick: (goal: GoalNames) => void;
  isDisabled?: boolean;
};

export const GoalTile = ({ title, name, Icon, isSelected, onClick, isDisabled }: GoalTileProps) => {
  const theme = useTheme();

  if (isDisabled) {
    return (
      <div css={disabledTile({ theme })}>
        <div>{Icon && <Icon width={30} height={30} color="support" />}</div>
        <Typography2 component="p" variant="bodyPrimary" align="center" color="supportText">
          {title}
        </Typography2>
      </div>
    );
  }

  return (
    <TrackedDiv
      css={tile({ theme, isSelected })}
      role="button"
      aria-label={title}
      tabIndex={0}
      onClick={() => onClick(name)}
      trackingName="goal_selection_tile"
      data={{ type: isSelected ? 'deselect' : 'select', title }}
      data-testid="goal-selection-tile"
    >
      <div>{Icon && <Icon width={30} height={30} color="default" />}</div>
      <Typography2 component="p" variant="bodyPrimary" align="center">
        {title}
      </Typography2>
    </TrackedDiv>
  );
};
